import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { useUserActivityQuery } from "../hooks/useUserActivity";

const PAGE_SIZE = 20;

type SortField = "user" | "action" | "date" | null;
type SortOrder = "asc" | "desc";

const UserActivityPage = () => {
  const [user, setUser] = useState("");
  const [action, setAction] = useState("");
  const [status, setStatus] = useState<"all" | "success" | "warning" | "error">("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const { data, isLoading, isFetching } = useUserActivityQuery({
    user: user || undefined,
    action: action || undefined,
    status,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    page,
    pageSize: PAGE_SIZE,
    sortBy: sortField || undefined,
    sortOrder: sortField ? sortOrder : undefined,
  });

  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const events = data?.data ?? [];
  const isBusy = isLoading || isFetching;

  const getStatusBadge = (value: "success" | "warning" | "error") => {
    const config = {
      success: { className: "bg-green-500/20 text-green-400", label: "Exitoso" },
      warning: { className: "bg-yellow-500/20 text-yellow-400", label: "Advertencia" },
      error: { className: "bg-red-500/20 text-red-400", label: "Error" },
    };
    const { className, label } = config[value];
    return <Badge className={className}>{label}</Badge>;
  };

  const headerLabel = useMemo(() => {
    if (!total) return "0 eventos";
    if (total === 1) return "1 evento";
    return `${total} eventos`;
  }, [total]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-neutral-500">↕</span>;
    }
    return <span className="ml-1 text-purple-400">{sortOrder === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Actividad de usuarios"
        subtitle="Audita eventos críticos y monitorea el uso de la plataforma."
      />

      {/* Filters */}
      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-end">
          <div className="flex flex-1 flex-col gap-2">
            <Label className="text-xs text-neutral-400">Usuario</Label>
            <Input
              type="search"
              value={user}
              onChange={(e) => {
                setUser(e.target.value);
                setPage(1);
              }}
              placeholder="Correo o nombre"
              disabled={isBusy}
              className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Label className="text-xs text-neutral-400">Acción</Label>
            <Input
              type="search"
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
              placeholder="Nombre de evento"
              disabled={isBusy}
              className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-xs text-neutral-400">Estado</Label>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as typeof status);
                setPage(1);
              }}
              disabled={isBusy}
            >
              <SelectTrigger className="w-[140px] border-neutral-700 bg-neutral-800/50 text-neutral-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-neutral-700 bg-neutral-900">
                <SelectItem value="all" className="text-neutral-200">
                  Todos
                </SelectItem>
                <SelectItem value="success" className="text-neutral-200">
                  Exitosos
                </SelectItem>
                <SelectItem value="warning" className="text-neutral-200">
                  Advertencias
                </SelectItem>
                <SelectItem value="error" className="text-neutral-200">
                  Errores
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-xs text-neutral-400">Desde</Label>
            <Input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              disabled={isBusy}
              className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-xs text-neutral-400">Hasta</Label>
            <Input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              disabled={isBusy}
              className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
            />
          </div>
          <Badge variant="outline" className="border-neutral-700 text-neutral-400">
            {headerLabel}
          </Badge>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-neutral-800 bg-neutral-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead
                className="cursor-pointer text-neutral-400 hover:text-neutral-200"
                onClick={() => handleSort("user")}
              >
                <div className="flex items-center">
                  Usuario
                  <SortIndicator field="user" />
                </div>
              </TableHead>
              <TableHead className="text-neutral-400">Dirección</TableHead>
              <TableHead
                className="cursor-pointer text-neutral-400 hover:text-neutral-200"
                onClick={() => handleSort("action")}
              >
                <div className="flex items-center">
                  Acción
                  <SortIndicator field="action" />
                </div>
              </TableHead>
              <TableHead className="text-neutral-400">Recurso</TableHead>
              <TableHead className="text-neutral-400">Estado</TableHead>
              <TableHead
                className="cursor-pointer text-neutral-400 hover:text-neutral-200"
                onClick={() => handleSort("date")}
              >
                <div className="flex items-center">
                  Fecha
                  <SortIndicator field="date" />
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((activity) => {
              const accountIdentifier =
                (activity.metadata as { accountIdentifier?: string | null } | undefined)
                  ?.accountIdentifier ?? null;

              return (
                <TableRow key={activity.id} className="border-neutral-800 hover:bg-neutral-800/50">
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-neutral-100">{activity.userName}</span>
                      <span className="text-xs text-neutral-400">{activity.userEmail}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="rounded bg-neutral-800 px-2 py-1 font-mono text-xs text-neutral-300">
                      {accountIdentifier ?? "—"}
                    </code>
                  </TableCell>
                  <TableCell className="text-neutral-200">{activity.action}</TableCell>
                  <TableCell className="text-neutral-300">{activity.resource}</TableCell>
                  <TableCell>{getStatusBadge(activity.status)}</TableCell>
                  <TableCell className="text-neutral-300">
                    {format(new Date(activity.createdAt), "dd 'de' MMM yyyy HH:mm", {
                      locale: es,
                    })}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {isBusy && <LoadingOverlay />}

        {!isBusy && events.length === 0 && (
          <EmptyState
            title="Sin actividad registrada"
            description="No existen eventos para los filtros seleccionados. Ajusta los parámetros para ampliar la búsqueda."
            className="m-6"
          />
        )}

        <div className="flex items-center justify-between border-t border-neutral-800 px-4 py-3">
          <span className="text-sm text-neutral-400">
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page === 1 || isBusy}
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page === totalPages || isBusy}
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
            >
              Siguiente
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UserActivityPage;
